import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useAuth } from '../../contexts/AuthContext';
import ParticlesAuth from '../AuthenticationInner/ParticlesAuth';
import { authApi } from '../../services/api';
import { formHatasiCoz } from '../../helpers/formHatasi';
import { toast } from 'react-toastify';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user, error: authError, clearError } = useAuth();
  // [KAYIT-GIRIS] (19 Eki 2026) kayit 409 "Sign in" baglantisi e-postayi router state ile tasir (URL'ye yazilmaz: nginx
  // access log / tarayici gecmisi / Referer'a dusmez); yalniz ilk yuklemede okunur, sonra kullanici degistirebilir.
  const location = useLocation();
  const gelenState: any = location.state;
  const [onDoluEposta] = useState<string>(() =>
    typeof gelenState?.email === 'string' ? gelenState.email.trim().slice(0, 254) : ''
  );

  // [KAYIT-GIRIS] onceki sayfanin (kayit) hata metni giris kutusuna tasinmaz; kayittan geliyorsa eski hata bildirimleri kapanir
  useEffect(() => {
    if (clearError) clearError();
    if (gelenState?.kaynak === 'kayit') toast.dismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [passwordShow, setPasswordShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      const data = await authApi.googleLogin();

      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        setError('Google login is not configured. Please try again later.');
      }
    } catch (err) {
      console.error('Failed to initiate Google login:', err);
      setError('Failed to connect to Google. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Redirect if already authenticated (page refresh while logged in)
  useEffect(() => {
    if (isAuthenticated && user) {
      // Redirect based on user role
      if (user.is_admin || user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: onDoluEposta,
      password: '',
      remember: false,
    },
    // [KAYIT-GIRIS] dokunulmamis / yalniz odaklanip birakilmis alanda hata gosterilmez: dogrulama gonderimde
    // (alan hatalari submitCount > 0 iken gorunur). Chrome otomatik doldurmayi kullanici etkilesimine kadar JS'den gizler;
    // blur dogrulamasi gorunurde dolu alana "Please enter..." yaziyordu.
    validateOnBlur: false,
    validationSchema: Yup.object({
      username: Yup.string().required('Please enter your username or email'),
      password: Yup.string().required('Please enter your password'),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        await login({
          username: values.username,
          password: values.password,
        });

        // Navigation will be handled by useEffect above
      } catch (err: any) {
        // [KAYIT-HATA] ortak cozumleyici: 422 (or. e-posta bicimi) alanin altinda, 429/ag/500 sade metin; form silinmez
        const h = formHatasiCoz(err, { alanAdlari: ['email', 'password'] });
        if (h.alanlar.email) validation.setFieldError('username', h.alanlar.email);
        if (h.alanlar.password) validation.setFieldError('password', h.alanlar.password);
        setError(h.alanlar.email || h.alanlar.password || h.genel || 'Login failed. Please check your credentials.');
      } finally {
        setLoading(false);
      }
    },
  });

  const alanHatasi = (ad: 'username' | 'password') =>
    validation.submitCount > 0 && validation.errors[ad] ? validation.errors[ad] : null;

  document.title = 'Login | Coderom Dropshipping Platform';

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-flex align-items-center gap-2 text-decoration-none">
                      <img src="/logo-full-white.png" alt="Coderom" style={{ height: '50px', width: 'auto' }} />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">
                    Professional AI-Powered Listing & Repricing Automation Platform
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Welcome Back! 👋</h5>
                      <p className="text-muted">
                        Sign in to continue to your dashboard
                      </p>
                    </div>

                    {/* Error Alert */}
                    {(error || authError) && (
                      <Alert color="danger" className="mt-3">
                        <i className="ri-error-warning-line me-2"></i>
                        {error || authError}
                      </Alert>
                    )}

                    {/* Google Sign In Button */}
                    <div className="mt-4">
                      <Button
                        color="light"
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                        onClick={handleGoogleLogin}
                        disabled={googleLoading}
                        style={{
                          border: '1px solid #e5e7eb',
                          padding: '10px 16px',
                          fontWeight: 500,
                        }}
                      >
                        {googleLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        {googleLoading ? 'Connecting...' : 'Sign in with Google'}
                      </Button>
                    </div>

                    {/* Divider */}
                    <div className="mt-4 text-center position-relative">
                      <div style={{ borderTop: '1px solid #e5e7eb' }}></div>
                      <span
                        className="bg-white px-2 text-muted"
                        style={{
                          position: 'relative',
                          top: '-12px',
                          fontSize: '13px',
                        }}
                      >
                        or sign in with email
                      </span>
                    </div>

                    <div className="p-2">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        {/* Username/Email */}
                        <div className="mb-3">
                          <Label htmlFor="username" className="form-label">
                            Username or Email
                          </Label>
                          <Input
                            name="username"
                            className="form-control"
                            placeholder="Enter username or email"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.username || ''}
                            invalid={
                              alanHatasi('username') ? true : false
                            }
                          />
                          {alanHatasi('username') ? (
                            <FormFeedback type="invalid">
                              {validation.errors.username}
                            </FormFeedback>
                          ) : null}
                        </div>

                        {/* Password */}
                        <div className="mb-3">
                          <div className="float-end">
                            <Link
                              to="/forgot-password"
                              className="text-muted text-decoration-underline"
                            >
                              Forgot password?
                            </Link>
                          </div>
                          <Label className="form-label" htmlFor="password-input">
                            Password
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              value={validation.values.password || ''}
                              type={passwordShow ? 'text' : 'password'}
                              className="form-control pe-5"
                              placeholder="Enter password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                alanHatasi('password') ? true : false
                              }
                            />
                            {alanHatasi('password') ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i
                                className={
                                  passwordShow
                                    ? 'ri-eye-off-fill align-middle'
                                    : 'ri-eye-fill align-middle'
                                }
                              ></i>
                            </button>
                          </div>
                        </div>

                        {/* Remember Me */}
                        <div className="form-check">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="auth-remember-check"
                            name="remember"
                            onChange={validation.handleChange}
                            checked={validation.values.remember}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="auth-remember-check"
                          >
                            Remember me
                          </Label>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-4">
                          <Button
                            color="primary"
                            disabled={loading}
                            className="btn btn-primary w-100"
                            type="submit"
                          >
                            {loading ? (
                              <>
                                <Spinner size="sm" className="me-2">
                                  {' '}
                                  Loading...{' '}
                                </Spinner>
                                Signing In...
                              </>
                            ) : (
                              <>
                                <i className="ri-login-box-line me-1"></i> Sign
                                In
                              </>
                            )}
                          </Button>
                        </div>

                        {/* Social Login - Optional */}
                        {/* <div className="mt-4 text-center">
                          <div className="signin-other-title">
                            <h5 className="fs-13 mb-4 title">Sign In with</h5>
                          </div>
                          <div>
                            <Button color="primary" className="btn-icon me-1">
                              <i className="ri-facebook-fill fs-16"></i>
                            </Button>
                            <Button color="danger" className="btn-icon me-1">
                              <i className="ri-google-fill fs-16"></i>
                            </Button>
                            <Button color="dark" className="btn-icon">
                              <i className="ri-github-fill fs-16"></i>
                            </Button>
                          </div>
                        </div> */}
                      </Form>
                    </div>
                  </CardBody>
                </Card>

                {/* Sign Up Link */}
                <div className="mt-4 text-center">
                  <p className="mb-0">
                    Don't have an account?{' '}
                    <Link
                      to="/register"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      {' '}
                      Sign Up{' '}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default Login;
